// Hands patch for Dawn 11: keep the drawer empty state synchronized after AJAX cart updates.
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.drawerRefreshPromise = null;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.syncEmptyState();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.boundClose);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, options = {}) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (options.refresh === false) {
      this.showDrawer();
      return;
    }

    this.refreshFromServer()
      .catch((error) => {
        console.error('Nao foi possivel sincronizar o carrinho antes de abrir.', error);
      })
      .finally(() => this.showDrawer());
  }

  showDrawer() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const preferredContainer = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const containerToTrapFocusOn = preferredContainer || this.querySelector('.drawer__inner');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  async refreshFromServer() {
    if (this.drawerRefreshPromise) return this.drawerRefreshPromise;

    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cartUrl = (window.routes && window.routes.cart_url) || `${rootUrl}cart`;
    const separator = cartUrl.includes('?') ? '&' : '?';

    this.drawerRefreshPromise = Promise.all([
      fetch(`${rootUrl}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      }),
      fetch(`${cartUrl}${separator}section_id=cart-drawer&_=${Date.now()}`, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        cache: 'no-store',
      }),
    ])
      .then(async ([cartResponse, drawerResponse]) => {
        if (!cartResponse.ok) throw new Error(`Falha ao consultar o carrinho: ${cartResponse.status}`);

        const cart = await cartResponse.json();

        if (drawerResponse.ok) {
          const responseText = await drawerResponse.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const freshDrawer = html.querySelector('cart-drawer');
          const currentContent = this.querySelector('#CartDrawer');
          const freshContent = freshDrawer && freshDrawer.querySelector('#CartDrawer');

          if (currentContent && freshContent) {
            currentContent.innerHTML = freshContent.innerHTML;
            this.bindOverlay();
          }
        }

        const isEmpty = Number(cart.item_count || 0) === 0;
        this.syncEmptyState(isEmpty);
        document.dispatchEvent(
          new CustomEvent('hands:cart-refreshed', {
            detail: { itemCount: Number(cart.item_count || 0), isEmpty },
          })
        );

        return cart;
      })
      .finally(() => {
        this.drawerRefreshPromise = null;
      });

    return this.drawerRefreshPromise;
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerSection = parsedState.sections && parsedState.sections['cart-drawer'];
    const freshDrawer = drawerSection ? this.getSectionDOM(drawerSection, 'cart-drawer') : null;
    const isEmpty = freshDrawer
      ? freshDrawer.classList.contains('is-empty')
      : typeof parsedState.item_count === 'number'
      ? parsedState.item_count === 0
      : false;

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);

      if (sectionElement && innerHTML !== null) {
        sectionElement.innerHTML = innerHTML;
      }
    });

    this.syncEmptyState(isEmpty);

    setTimeout(() => {
      this.bindOverlay();
      this.open(undefined, { refresh: false });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = this.getSectionDOM(html, selector);
    return section ? section.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  syncEmptyState(isEmpty) {
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    const resolvedEmptyState =
      typeof isEmpty === 'boolean'
        ? isEmpty
        : cartDrawerItems
        ? cartDrawerItems.classList.contains('is-empty')
        : Boolean(this.querySelector('.drawer__inner-empty'));

    this.classList.toggle('is-empty', resolvedEmptyState);

    if (cartDrawerItems) {
      cartDrawerItems.classList.toggle('is-empty', resolvedEmptyState);
    }

    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (checkoutButton) {
      checkoutButton.disabled = resolvedEmptyState;
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
